import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../app/store';

export interface ProjectPendingCounts {
  tasks: {
    rejected: number;
    waiting: number;
  };
  hourCards: {
    rejected: number;
    waiting: number;
  };
}

// Define a type for the slice state
interface ProjectPendingState {
  value: ProjectPendingCounts | null;
}

// Define the initial state using that type
const initialState: ProjectPendingState = {
  value: null
};

export const projectPendingSlice = createSlice({
  name: 'projectPending',
  initialState,
  reducers: {
    updateProjectPending: (state, action: PayloadAction<ProjectPendingCounts | null>) => {
      state.value = action.payload;
    },
    /** Sync one module's counts from its page; stays null when the project needs no approvals */
    setModulePending: (
      state,
      action: PayloadAction<{ module: keyof ProjectPendingCounts; counts: ProjectPendingCounts['tasks'] }>
    ) => {
      if (state.value) {
        state.value[action.payload.module] = action.payload.counts;
      }
    },
    incrementTasksWaiting: state => {
      if (state.value) {
        state.value.tasks.waiting++;
      }
    },
    decrementTasksWaiting: state => {
      if (state.value && state.value.tasks.waiting > 0) {
        state.value.tasks.waiting--;
      }
    },
    incrementTasksRejected: state => {
      if (state.value) {
        state.value.tasks.rejected++;
      }
    },
    decrementTasksRejected: state => {
      if (state.value && state.value.tasks.rejected > 0) {
        state.value.tasks.rejected--;
      }
    },
    incrementHourCardsWaiting: state => {
      if (state.value) {
        state.value.hourCards.waiting++;
      }
    },
    decrementHourCardsWaiting: state => {
      if (state.value && state.value.hourCards.waiting > 0) {
        state.value.hourCards.waiting--;
      }
    },
    incrementHourCardsRejected: state => {
      if (state.value) {
        state.value.hourCards.rejected++;
      }
    },
    decrementHourCardsRejected: state => {
      if (state.value && state.value.hourCards.rejected > 0) {
        state.value.hourCards.rejected--;
      }
    }
  }
});

export const {
  updateProjectPending,
  setModulePending,
  incrementTasksWaiting,
  decrementTasksWaiting,
  incrementTasksRejected,
  decrementTasksRejected,
  incrementHourCardsWaiting,
  decrementHourCardsWaiting,
  incrementHourCardsRejected,
  decrementHourCardsRejected
} = projectPendingSlice.actions;

// Selector
export const selectProjectPending = (state: RootState) => state.projectPending.value;

export default projectPendingSlice.reducer;
